// MapComponent.tsx
import React, { useEffect, useState } from 'react'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import L, { LatLngExpression } from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { CurrentSession } from '@/services/repository/session'
import { Services } from '@/services/services'
import { useNavigate } from 'react-router-dom'
import { useModal } from '../modal/Modal'
import { SuperAdminInsertMachinesPopup } from '@/pages/dashboard/superadmin/machines/Machines'
import { Roles } from '@/services/repository/users'
import { defaultMapCenter } from '@/env/config'
import { formatDate } from '@/common/time'

import './Map.css'

interface MapRowMachine {
  id: string | undefined
  name: string | undefined
}

interface MapRow {
  imei: string
  time: string // ISO 8601 string representation of DtServer (e.g., "2025-01-17T12:34:56Z")
  dtTracker: string // ISO 8601 string representation of DtTracker (e.g., "2025-01-17T12:34:56Z")
  lat: number  // Latitude as a floating-point number
  lng: number  // Longitude as a floating-point number
  altitude: number // Altitude in meters
  angle: number // Angle in degrees
  speed: number // Speed in meters per second
  machine: MapRowMachine
}

interface MapResponse {
  time: string // ISO 8601 string representation of the latest DtServer
  rows: MapRow[] | null   // Array of MapRow objects
}

function mergeMapResponses(oldResponse: MapResponse, newResponse: MapResponse): MapResponse {
  if (!newResponse.rows) {
    return oldResponse
  }
  const updatedRows: MapRow[] = oldResponse.rows ? [...oldResponse.rows] : []
  const imeiMap = new Map<string, MapRow>(updatedRows.map(row => [row.imei, row]))
  for (const newRow of newResponse.rows) {
    imeiMap.set(newRow.imei, newRow)
  }
  const mergedRows = Array.from(imeiMap.values());
  return {
    time: newResponse.time,
    rows: mergedRows,
  }
}


export const MapComponent = ({ services, session, fetchUrl }: {
  services: Services
  session: CurrentSession
  fetchUrl: string
}) => {
  const [map, setMap] = useState<MapResponse | undefined>(undefined)
  const [myposition, setMyPosition] = useState<[number, number] | undefined>(undefined)
  const navigate = useNavigate()
  const { openModal } = useModal()

  const now = Date.now()
  const activeTime = 5 * 60 * 1000; // fiveMinutesInMs

  function isActive(dateStr: string): boolean {
    const inputDate = new Date(dateStr).getTime();
    if (isNaN(inputDate)) {
      throw new Error("Invalid date string");
    }
    return now - inputDate <= activeTime && inputDate <= now;
  }

  async function refreshMap(fromDate?: Date, oldResponse?: MapResponse): Promise<MapResponse> {
    return services.session.fetchWithToken(`${fetchUrl}?fromDate=${fromDate ? fromDate.toISOString() : ''}`).then(async (r) => {
      if (r.status !== 200) {
        const erros = await r.json()
        console.error(erros)
        return Promise.reject<MapResponse>()
      }
      const newResponse: MapResponse = await r.json()
      newResponse.rows = newResponse.rows || null
      if (oldResponse === undefined) {
        return newResponse
      }
      return mergeMapResponses(oldResponse, newResponse)
    })
  }

  useEffect(() => {
    // Function to handle geolocation success
    const handleSuccess = (location: GeolocationPosition) => {
      const { latitude, longitude } = location.coords
      setMyPosition([latitude, longitude])
    }

    // Function to handle geolocation error
    const handleError = (error: GeolocationPositionError) => {
      console.error('Error obtaining location:', error)
      // Fallback to a default position if geolocation fails
      setMyPosition(undefined)
    }

    // Check if geolocation is available
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(handleSuccess, handleError)
    } else {
      console.error('Geolocation is not supported by this browser.')
      // Fallback to a default position if geolocation is not supported
      setMyPosition(undefined)
    }
  }, [])

  useEffect(() => {
    refreshMap().then(setMap)
  }, []);

  useEffect(() => {
    if (map === undefined) {
      return
    }
    const clock = async () => {
      if (map === undefined) {
        return
      }
      await refreshMap(map?.time ? new Date(map?.time) : undefined, map).then(setMap)
      handle = setTimeout(clock, 2000)
    }
    let handle: NodeJS.Timeout = setTimeout(clock, 2000)
    return () => {
      clearTimeout(handle)
    }
  }, [map])

  if (map === undefined) {
    return <div className='spinner'></div>
  }

  function findLatestRow(response: MapResponse | undefined): MapRow | undefined {
    if (!response?.rows || response.rows.length === 0) {
      return undefined;
    }
  
    return response.rows.reduce((latest, current) => {
      return new Date(current.time) > new Date(latest.time) ? current : latest;
    });
  }

  const firstRow = findLatestRow(map);

  const center: LatLngExpression = (firstRow && {
    lat: firstRow.lat,
    lng: firstRow.lng
  }) || myposition || defaultMapCenter

  return (
    <MapContainer attributionControl={false} center={center} zoom={13} style={{ height: '100%', width: '100%' }}>
      <TileLayer
        attribution='evoltag.pl'
        url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
      />
      {map.rows?.map((row) => (
        <Marker
          key={row.imei}
          position={row}
          icon={L.divIcon({
            className: 'car-icon',
            html: row.angle ? `<img src="/public/img/gps/vehicle_arrow.png" style="transform: rotate(${row.angle + 45}deg); opacity: ${isActive(row.dtTracker) ? '1' : '0.33'};width: 16px; margin: 8px" />` : `<img src="/public/img/gps/map_point.png" style="transform: rotate(${row.angle + 45}deg); opacity: ${isActive(row.dtTracker) ? '1' : '0.33'}; width: 16px; margin: 8px" />`,
            iconSize: [32, 32], // Adjust size as needed
            iconAnchor: [16, 16], // Anchor point
          })} >
          <Popup>
            <div onClick={() => row.machine.id && navigate(`/communal/machine/${row.machine.id}`)}>

              {/* <a className='main-btn' onClick={}></a> */}

              {!row.machine.id ? <p style={{ cursor: 'pointer' }} onClick={() => session.user.role === Roles.SuperAdmin && openModal((props) => SuperAdminInsertMachinesPopup({
                ...props, services, session, defaultValues: {
                  imei: row.imei,
                }, onInsert: ({ id }) => {
                  navigate(`/communal/machine/${id}`)
                }
              }))}>{`IMEI: ${row.imei}`}</p> : null}
              {row.machine.id ? <p style={{ cursor: 'pointer' }}>{row.machine.name}</p> : null}
              <p>{session.translates.for('map.speed')}: {row.speed} km/h</p>
              <p>{session.translates.for('map.direction')}: {row.angle}°</p>
              <p className='map-point-dttracker'>{formatDate(new Date(row.dtTracker))}</p>
              {session.user.role === Roles.SuperAdmin && !row.machine.id ? <p className='main-btn' onClick={() => openModal((props) => SuperAdminInsertMachinesPopup({
                ...props, services, session, defaultValues: {
                  imei: row.imei,
                }, onInsert: ({ id }) => {
                  navigate(`/communal/machine/${id}`)
                }
              }))}>{session.translates.for('map.add_btn')}</p> : null}
              
              {row.machine.id ? <p className='main-btn' onClick={() => navigate(`/communal/machine/${row.machine.id}`)}>{session.translates.for('map.machine_page')}</p> : null}
            </div>
          </Popup>
        </Marker>
      ))}

      {myposition ? <Marker
        key='my-location'
        position={myposition} 
        icon={L.divIcon({
          className: 'car-icon',
          html: `<img src="/public/img/gps/i_am_here.png" style="width: 32px; margin: -16px auto auto 0px" />`,
          iconSize: [32, 32], // Adjust size as needed
          iconAnchor: [16, 16], // Anchor point
        })}></Marker> : null}
    </MapContainer>
  )
}

export default MapComponent
